import { useState, useEffect } from "react";
import { X } from "lucide-react";

export default function UserModal({ open, onClose, onSave, editUser, isLoading, apiError, viewOnly }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "admin",
    contactNumber: "",
  });
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (open) {
      setLocalError("");
      if (editUser) {
        setForm({
          name: editUser.name || "",
          email: editUser.email || "",
          role: editUser.role || "admin",
          contactNumber: editUser.phoneNumber || editUser.contactNumber || "",
        });
      } else {
        setForm({ name: "", email: "", role: "admin", contactNumber: "" });
      }
    }
  }, [open, editUser]);

  if (!open) return null;

  const isNew = !editUser;

  const handleSave = () => {
    setLocalError("");
    if (!form.name.trim()) {
      setLocalError("Name is required.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim())) {
      setLocalError("Please enter a valid email address.");
      return;
    }
    const phoneDigits = form.contactNumber.replace(/\D/g, "");
    if (phoneDigits.length < 10 || phoneDigits.length > 15) {
      setLocalError("Please enter a valid 10-digit contact number.");
      return;
    }
    onSave(form);
  };

  const inputCls = "w-full bg-white border border-[#00000059] rounded-[8px] h-[50px] px-4 outline-none text-gray-800 text-[14px] placeholder:text-gray-400 disabled:opacity-60";
  const lblCls = "block text-[13px] font-semibold text-[#00000099] mb-1.5";

  return (
    <div
      onClick={isLoading ? undefined : onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0000001A] backdrop-blur-[14px]"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[660px] mx-4 p-7 relative bg-[#E7F0F1] border border-[#00000033] backdrop-blur-[20px] shadow-[0px_0px_32px_0px_#3AA1A333] rounded-[20px]"
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-[22px] font-bold text-gray-900 m-0 leading-tight">
              {viewOnly ? "User Details" : (editUser ? "Edit User" : "Add User")}
            </h2>
            <p className="text-[13px] text-gray-400 mt-1">
              {viewOnly ? "View user details below." : (editUser ? "Update user details below." : "Add a new user below.")}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors border-none bg-transparent cursor-pointer disabled:opacity-50 text-gray-500 shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* API error banner */}
        {(apiError || localError) && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600 font-medium">
            {apiError || localError}
          </div>
        )}

        {/* ── Responsive form grid ────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4 mb-6">
          {/* Name */}
          <div>
            <label className={lblCls}>Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. John Doe"
              disabled={isLoading || viewOnly}
              className={inputCls}
            />
          </div>

          {/* Roles */}
          <div>
            <label className={lblCls}>Roles</label>
            <select
              value={form.role}
              onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}
              disabled={isLoading || !isNew || viewOnly}
              className={`${inputCls} cursor-pointer appearance-none`}
            >
              {isNew ? (
                <>
                  <option value="" disabled>Select a role</option>
                  <option value="admin">Admin</option>
                  <option value="reviewer">Reviewer</option>
                  <option value="agent">Agent</option>
                </>
              ) : (
                <>
                  <option value="admin">Admin</option>
                  <option value="reviewer">Reviewer</option>
                  <option value="editor">Editor</option>
                  <option value="moderator">Moderator</option>
                  <option value="data_collection_agent">Data Collection Agent</option>
                  <option value="super_admin">Super Admin</option>
                </>
              )}
            </select>
          </div>

          {/* Email */}
          <div>
            <label className={lblCls}>Email <span className="text-red-500">*</span></label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              placeholder="john@company.com"
              disabled={isLoading || !isNew || viewOnly}
              className={inputCls}
            />
          </div>

          {/* Contact Number */}
          <div>
            <label className={lblCls}>Contact Number <span className="text-red-500">*</span></label>
            <input
              type="tel"
              value={form.contactNumber}
              onChange={(e) => setForm((p) => ({ ...p, contactNumber: e.target.value }))}
              placeholder="+91 00000-00000"
              disabled={isLoading || viewOnly}
              className={inputCls}
            />
          </div>
        </div>

        {/* ── Footer buttons ──────────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-3">
          {viewOnly ? (
            <button
              onClick={onClose}
              className="h-[44px] px-8 bg-[#0B6172] border border-[#0B617233] rounded-[8px] text-[14px] font-semibold text-white cursor-pointer hover:bg-[#094f5e] transition-colors"
            >
              Close
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={isLoading}
                className="h-[44px] px-8 bg-[#FFFFFF] border border-[#0B617233] rounded-[8px] text-[14px] font-semibold text-[#5E5E5E] cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isLoading || !form.name.trim() || !form.email.trim() || !form.contactNumber.trim()}
                className="h-[44px] px-8 bg-[#0B6172] border border-[#0B617233] rounded-[8px] text-[14px] font-semibold text-white cursor-pointer hover:bg-[#094f5e] transition-colors disabled:opacity-50"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {editUser ? "Saving..." : "Creating..."}
                  </span>
                ) : (
                  editUser ? "Save" : "Approve"
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
